import {Injectable} from '@angular/core';
import {AngularFirestoreCollection} from '@angular/fire/compat/firestore';
import {AbstractFirestoreService} from '@shared/services/abstracts/AbstractFirestoreService';
import {AuthService} from '@shared/services/auth.service';
import type {VatReturn} from '@vat/engine/types';
import firebase from 'firebase/compat';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {normalizeUndefinedToNull, normalizeVatReturnDates} from './vat-return.factory';
import WriteBatch = firebase.firestore.WriteBatch;

/** Zeilen-Chunk `vatReturns/{id}/lines/{chunk_000…}` (Spec 3.4, ≤ 400 Zeilen je Dokument). */
export interface VatReturnLinesChunk {
    index: number;
    lines: unknown[];
}

/**
 * Firestore-Zugriff auf `vatReturns/{id}` (Root; Pool-Präfix `customers/{cid}/` über buildFirestorePath).
 * Ansehen schreibt nichts – das Dokument entsteht beim ersten Schreibvorgang (Spec 3.4).
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
@Injectable({providedIn: 'root'})
export class VatReturnsService extends AbstractFirestoreService<VatReturn> {

    COLLECTION = 'vatReturns';

    constructor(authService: AuthService) {
        super(authService);
    }

    /** Single-Field-Merge-Query (Spec 3.8: kein Composite-Index), Sortierung im Client; Timestamps → Date an der Lesegrenze. */
    listByLandlordAndYear(landlordID: string, year: number): Observable<VatReturn[]> {
        return this.collection(ref => ref.where('landlordID', '==', landlordID).where('year', '==', year))
            .valueChanges({idField: 'id'})
            .pipe(map(list => list.map(raw => normalizeVatReturnDates(raw)).sort((a, b) =>
                (a.periodKey ?? '').localeCompare(b.periodKey ?? '') || (a.version ?? 0) - (b.version ?? 0))));
    }

    /**
     * Landing v2 (Spec 2026-09-13 Sektion 1.2): alle vatReturns eines Jahres in EINER Gleichheits-Query,
     * Gruppierung nach landlordID im Client (`groupReturnsByLandlord`). Einmal-Snapshot (`get`), Timestamps → Date.
     */
    listByYear(year: number): Observable<VatReturn[]> {
        return this.collection(ref => ref.where('year', '==', year)).get().pipe(
            map(snapshot => snapshot.docs.map(doc => normalizeVatReturnDates({...(doc.data() as object), id: doc.id}) as VatReturn)),
        );
    }

    createInBatch(batch: WriteBatch, vatReturn: VatReturn): WriteBatch {
        batch.set(this.doc(vatReturn.id).ref, normalizeUndefinedToNull(vatReturn));
        return batch;
    }

    create(vatReturn: VatReturn): Promise<void> {
        return this.createInBatch(this.batch(), vatReturn).commit();
    }

    /** Teil-Update mit dotted keys; ergänzt Audit-Felder. Kein removeEmpty (FieldValue-sicher). */
    updateFields(id: string, patch: Record<string, unknown>): Promise<void> {
        return this.doc(id).update({
            ...normalizeUndefinedToNull(patch),
            updatedAt: new Date(),
            updatedBy: this.authService.authUser?.uid ?? null,
        } as Partial<VatReturn>);
    }

    linesCollection(returnID: string): AngularFirestoreCollection<VatReturnLinesChunk> {
        return this.authService.afs.collection<VatReturnLinesChunk>(this.buildFirestorePath(`${this.COLLECTION}/${returnID}/lines`));
    }
}
